import { useEffect, useState } from "react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Radio,
  RadioGroup,
} from "@heroui/react";
import { RiUserLine } from "react-icons/ri";
import dayjs from "dayjs";
import { useCalendarStore } from "../../../store/useCalendarStore";
import { Appointment } from "./AppointmentActionsDropdown";
import { NoShowNotificationSwitch } from "./NoShowNotificationSwitch";

type NoShowModalProps = {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment;
  handleUpdateCustomerAppointments: (
    customerAppointments: {
      userAppointmentId: number;
      customerDidAttend: number;
    }[],
    options?: { sendNoShowNotification?: boolean },
  ) => void;
};

export const NoShowModal = ({
  isOpen,
  onClose,
  appointment,
  handleUpdateCustomerAppointments,
}: NoShowModalProps) => {
  const { customers } = appointment;
  const activeCustomers = customers.filter((c) => !c.isDeleted);

  const hasNoShowNotification = useCalendarStore(
    (state) => state.hasNoShowNotification,
  );

  const [sendNoShowNotification, setSendNoShowNotification] = useState(true);

  // Reset the "send notification" toggle every time the modal opens.
  // Without this, an off state from a previous no-show would persist across
  // sessions and silently suppress notifications on the next save.
  useEffect(() => {
    if (isOpen) setSendNoShowNotification(true);
  }, [isOpen]);

  const [customerStates, setCustomerStates] = useState(
    activeCustomers.map((customer) => ({
      userAppointmentId: customer.userAppointmentId,
      customerDidAttend: customer.customerDidAttend,
    })),
  );

  useEffect(() => {
    setCustomerStates(
      activeCustomers.map((customer) => ({
        userAppointmentId: customer.userAppointmentId,
        customerDidAttend: customer.customerDidAttend,
      })),
    );
  }, [customers]);

  const updateCustomerState = (
    userAppointmentId: number,
    customerDidAttend: number,
  ) => {
    setCustomerStates((prevStates) =>
      prevStates.map((state) =>
        state.userAppointmentId === userAppointmentId
          ? { ...state, customerDidAttend }
          : state,
      ),
    );
  };

  const isMarkingAnyNoShow = customerStates.some(
    (state) => state.customerDidAttend === -1,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="outside">
      <ModalContent>
        <ModalHeader>
          <Trans>Označi prisotnost</Trans>
        </ModalHeader>
        <ModalBody className="gap-5">
          <div className="bg-content2 rounded-large flex items-center justify-between gap-3 p-3">
            <span className="text-default-500 text-xs">
              {dayjs(appointment.startTime).format("dd, DD.MM.YYYY")}
            </span>
            <span className="text-sm leading-5 font-semibold">
              {dayjs(appointment.startTime).format("HH:mm")}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-default-500 text-xs font-medium">
              <Trans>Označite prisotnost strank</Trans>
            </span>

            <div className="flex flex-col gap-1">
              {activeCustomers.map((customer) => {
                const customerState = customerStates.find(
                  (state) =>
                    state.userAppointmentId === customer.userAppointmentId,
                );

                return (
                  <div
                    key={customer.userAppointmentId}
                    className="border-default-200 rounded-large flex flex-wrap items-center justify-between gap-3 border p-3"
                  >
                    <span className="flex min-w-0 items-center gap-1.5 text-sm leading-5">
                      <RiUserLine
                        className="text-default-500 shrink-0"
                        size={".85rem"}
                      />
                      {customer.name} {customer.lastName}
                    </span>

                    <RadioGroup
                      size="sm"
                      value={String(customerState?.customerDidAttend)}
                      onValueChange={(value) =>
                        updateCustomerState(
                          customer.userAppointmentId,
                          Number(value),
                        )
                      }
                      orientation="horizontal"
                      classNames={{ wrapper: "gap-4 flex-nowrap" }}
                    >
                      <Radio value="1">{t`Prisoten`}</Radio>
                      <Radio value="-1">{t`Neprihod`}</Radio>
                    </RadioGroup>
                  </div>
                );
              })}
            </div>
          </div>

          {hasNoShowNotification && isMarkingAnyNoShow ? (
            <NoShowNotificationSwitch
              isSelected={sendNoShowNotification}
              onValueChange={setSendNoShowNotification}
              selectedServices={appointment.appointmentParts.map((part) => ({
                serviceId: part.serviceId,
              }))}
              locationId={appointment.locationId}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={onClose}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            color="primary"
            onPress={() => {
              handleUpdateCustomerAppointments(customerStates, {
                sendNoShowNotification,
              });
              onClose();
            }}
          >
            <Trans>Shrani</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
